import { Quotation, LineItem } from './types';

const STORAGE_KEY = 'peaktime_quotations';

export function getQuotations(): Quotation[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : getSeedData();
  } catch {
    return getSeedData();
  }
}

export function saveQuotation(q: Quotation): void {
  const all = getQuotations();
  const idx = all.findIndex(x => x.id === q.id);
  if (idx >= 0) {
    all[idx] = { ...q, updatedAt: new Date().toISOString() };
  } else {
    all.unshift({ ...q, updatedAt: new Date().toISOString() });
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

export function deleteQuotation(id: string): void {
  const all = getQuotations().filter(q => q.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

export function getQuotationById(id: string): Quotation | undefined {
  return getQuotations().find(q => q.id === id);
}

export function generateQuoteNumber(): string {
  const all = getQuotations();
  const year = new Date().getFullYear();
  const seq = all.length + 1;
  return `PT/${year}/${String(seq).padStart(4, '0')}`;
}

function makeItem(overrides: Partial<LineItem> & { id: string }): LineItem {
  const qty = overrides.qty ?? 1;
  const rate = overrides.rate ?? 0;
  const gstRate = overrides.gstRate ?? 18;
  const amount = qty * rate;
  const gstAmount = (amount * gstRate) / 100;
  return {
    srNo: overrides.srNo ?? 1,
    description: overrides.description ?? '',
    hsn: overrides.hsn ?? '',
    unit: overrides.unit ?? 'Nos',
    qty,
    rate,
    gstRate,
    amount,
    gstAmount,
    total: amount + gstAmount,
    ...overrides,
  };
}

function getSeedData(): Quotation[] {
  const seed: Quotation[] = [
    {
      id: 'qt-001',
      quoteNumber: 'PT/2026/0001',
      date: '15/04/2026',
      validUntil: '15/05/2026',
      status: 'Approved',
      clientName: 'Rajesh Sharma',
      clientCompany: 'Sharma Enterprises Pvt Ltd',
      clientAddress: 'B-12, Okhla Industrial Area, Phase-II, New Delhi - 110020',
      clientPhone: '+91 9876543210',
      clientEmail: 'rajesh@sharmaenterprises.in',
      clientGSTIN: '07AABCS1429B1Z2',
      subject: 'Supply of LED Lighting Solutions',
      gstType: 'CGST_SGST',
      lineItems: [
        makeItem({ id: 'li-001-1', srNo: 1, description: 'LED Panel Light 36W (600x600mm)', hsn: '9405', qty: 20, rate: 2800, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-001-2', srNo: 2, description: 'LED Strip Light 5M RGB', hsn: '9405', qty: 10, rate: 1200, gstRate: 18, unit: 'Roll' }),
        makeItem({ id: 'li-001-3', srNo: 3, description: 'Installation & Commissioning', hsn: '9987', qty: 1, rate: 8500, gstRate: 18, unit: 'Job' }),
      ],
      subtotal: 74500,
      totalGST: 13410,
      grandTotal: 87910,
      terms: 'Payment within 30 days. Warranty: 2 years on all products.',
      notes: 'Prices inclusive of delivery to site. Installation by certified technicians.',
      preparedBy: 'Amit Kumar',
      createdAt: '2026-04-15T09:00:00.000Z',
      updatedAt: '2026-04-18T11:30:00.000Z',
    },
    {
      id: 'qt-002',
      quoteNumber: 'PT/2026/0002',
      date: '18/04/2026',
      validUntil: '18/05/2026',
      status: 'Sent',
      clientName: 'Priya Mehta',
      clientCompany: 'Mehta Construction Ltd',
      clientAddress: 'Plot 45, Sector 62, Noida, UP - 201301',
      clientPhone: '+91 9988776655',
      clientEmail: 'priya.mehta@mehtaconstruction.com',
      clientGSTIN: '09AADCM3456F1Z8',
      subject: 'Electrical Fittings for Residential Project',
      gstType: 'IGST',
      lineItems: [
        makeItem({ id: 'li-002-1', srNo: 1, description: 'MCB 32A Single Pole (Schneider)', hsn: '8536', qty: 50, rate: 320, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-002-2', srNo: 2, description: 'Distribution Board 8-Way', hsn: '8537', qty: 8, rate: 2200, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-002-3', srNo: 3, description: '2.5mm FR Cable (100m Roll)', hsn: '8544', qty: 15, rate: 3800, gstRate: 18, unit: 'Roll' }),
        makeItem({ id: 'li-002-4', srNo: 4, description: 'Wiring Accessories Set', hsn: '8536', qty: 20, rate: 450, gstRate: 18, unit: 'Set' }),
      ],
      subtotal: 91000,
      totalGST: 16380,
      grandTotal: 107380,
      terms: '50% advance, balance on delivery. No returns after installation.',
      notes: 'All products carry manufacturer warranty. Site visit included.',
      preparedBy: 'Sunita Verma',
      createdAt: '2026-04-18T14:00:00.000Z',
      updatedAt: '2026-04-20T10:00:00.000Z',
    },
    {
      id: 'qt-003',
      quoteNumber: 'PT/2026/0003',
      date: '20/04/2026',
      validUntil: '20/05/2026',
      status: 'Draft',
      clientName: 'Vikram Singh',
      clientCompany: 'Singh & Sons Hardware',
      clientAddress: '14/2, Lajpat Nagar-III, New Delhi - 110024',
      clientPhone: '+91 9811223344',
      clientEmail: 'vikram@singhsons.com',
      clientGSTIN: '07BBBCS9876A1Z1',
      subject: 'CCTV & Security System Supply',
      gstType: 'CGST_SGST',
      lineItems: [
        makeItem({ id: 'li-003-1', srNo: 1, description: '2MP IP Dome Camera (Hikvision)', hsn: '8525', qty: 8, rate: 4500, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-003-2', srNo: 2, description: '8-Channel NVR with 2TB HDD', hsn: '8521', qty: 1, rate: 18000, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-003-3', srNo: 3, description: 'Cat6 Cable (305m Box)', hsn: '8544', qty: 2, rate: 4200, gstRate: 18, unit: 'Box' }),
      ],
      subtotal: 62800,
      totalGST: 11304,
      grandTotal: 74104,
      terms: 'Payment on delivery. 1-year on-site warranty.',
      notes: 'AMC available at additional cost.',
      preparedBy: 'Amit Kumar',
      createdAt: '2026-04-20T16:00:00.000Z',
      updatedAt: '2026-04-20T16:00:00.000Z',
    },
    {
      id: 'qt-004',
      quoteNumber: 'PT/2026/0004',
      date: '22/04/2026',
      validUntil: '22/05/2026',
      status: 'Rejected',
      clientName: 'Neha Gupta',
      clientCompany: 'Gupta Infra Projects',
      clientAddress: 'B-5, Rohini Sector 11, New Delhi - 110085',
      clientPhone: '+91 9654321098',
      clientEmail: 'neha.gupta@guptainfra.in',
      clientGSTIN: '07CCCGS7654B1Z3',
      subject: 'Solar Panel Installation Quote',
      gstType: 'CGST_SGST',
      lineItems: [
        makeItem({ id: 'li-004-1', srNo: 1, description: '400W Mono PERC Solar Panel', hsn: '8541', qty: 10, rate: 12000, gstRate: 12, unit: 'Nos' }),
        makeItem({ id: 'li-004-2', srNo: 2, description: '5kW On-Grid Inverter', hsn: '8504', qty: 1, rate: 45000, gstRate: 18, unit: 'Nos' }),
      ],
      subtotal: 165000,
      totalGST: 22500,
      grandTotal: 187500,
      terms: 'Net 45 days. Subsidy documentation support included.',
      notes: 'MNRE approved products. Net metering support provided.',
      preparedBy: 'Sunita Verma',
      createdAt: '2026-04-22T11:00:00.000Z',
      updatedAt: '2026-04-24T09:00:00.000Z',
    },
    {
      id: 'qt-005',
      quoteNumber: 'PT/2026/0005',
      date: '25/04/2026',
      validUntil: '25/05/2026',
      status: 'Sent',
      clientName: 'Arjun Kapoor',
      clientCompany: 'Kapoor Real Estate',
      clientAddress: 'Tower A, DLF Cyber City, Gurugram, HR - 122002',
      clientPhone: '+91 9871234560',
      clientEmail: 'arjun@kapoorreal.com',
      clientGSTIN: '06DDDKK1234C1Z5',
      subject: 'Smart Home Automation Package',
      gstType: 'IGST',
      lineItems: [
        makeItem({ id: 'li-005-1', srNo: 1, description: 'Smart Switch Module 8-Gang', hsn: '8536', qty: 12, rate: 3200, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-005-2', srNo: 2, description: 'Smart Hub Controller', hsn: '8537', qty: 2, rate: 12000, gstRate: 18, unit: 'Nos' }),
        makeItem({ id: 'li-005-3', srNo: 3, description: 'Programming & Configuration', hsn: '9983', qty: 1, rate: 15000, gstRate: 18, unit: 'Job' }),
      ],
      subtotal: 77400,
      totalGST: 13932,
      grandTotal: 91332,
      terms: 'Advance 40%, balance after demo. 6-month support included.',
      notes: 'Remote monitoring app provided free for 1 year.',
      preparedBy: 'Amit Kumar',
      createdAt: '2026-04-25T10:00:00.000Z',
      updatedAt: '2026-04-25T10:00:00.000Z',
    },
  ];
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
  }
  return seed;
}